import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useThemeStore, Theme } from '@/store/theme.store';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PermissionGate } from '@/components/PermissionGate';
import { Moon, Sun, Monitor, Bell, Mail, Globe, Smartphone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { preferencesService } from '@/features/preferences/services/preferences.service';

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { theme, setTheme } = useThemeStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [language, setLanguage] = useState('pt-BR');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);

  // Carregar preferências da API
  const {
    data: preferences,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['preferences'],
    queryFn: () => preferencesService.getPreferences(),
  });

  // Atualizar estados quando as preferências forem carregadas
  useEffect(() => {
    if (preferences) {
      console.log('Preferências carregadas:', preferences);
      setEmailNotifications(preferences.emailNotifications ?? true);
      setPushNotifications(preferences.pushNotifications ?? false);
      setLanguage(preferences.language ?? 'pt-BR');
      setAutoRefresh(preferences.autoRefresh ?? true);
      setRefreshInterval(preferences.refreshInterval ?? 30);
    } else if (!isLoading && !isError) {
      // Se não há preferências e não está carregando, usar valores padrão
      console.log('Usando valores padrão para preferências');
    }
  }, [preferences, isLoading, isError]);

  // Mutation para atualizar preferências
  const updatePreferencesMutation = useMutation({
    mutationFn: (data: Parameters<typeof preferencesService.updatePreferences>[0]) =>
      preferencesService.updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preferences'] });
      toast({
        title: 'Configuração salva',
        description: 'Suas preferências foram atualizadas com sucesso',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao salvar configurações',
        variant: 'destructive',
      });
    },
  });

  const handleSaveEmailNotifications = () => {
    updatePreferencesMutation.mutate({
      emailNotifications,
    });
  };

  const handleSaveNotifications = () => {
    updatePreferencesMutation.mutate({
      pushNotifications,
    });
  };

  const handleSaveLanguage = () => {
    updatePreferencesMutation.mutate({
      language,
    });
  };

  const handleSaveAutoRefresh = () => {
    updatePreferencesMutation.mutate({
      autoRefresh,
      refreshInterval,
    });
  };

  const handleSaveTheme = () => {
    updatePreferencesMutation.mutate({
      preferences: {
        ...preferences?.preferences,
        theme,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Configurações</h1>
          <p className="text-muted-foreground">Configure as preferências da plataforma</p>
        </div>
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">Carregando configurações...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Configurações</h1>
          <p className="text-muted-foreground">Configure as preferências da plataforma</p>
        </div>
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-destructive">
              Erro ao carregar configurações. Tente novamente.
            </div>
            <div className="mt-4 text-center">
              <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['preferences'] })}>
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Renderizar conteúdo mesmo sem preferências (usará valores padrão)

  const themes: { value: Theme; label: string; icon: React.ReactNode }[] = [
    { value: 'light', label: 'Claro', icon: <Sun className="h-4 w-4" /> },
    { value: 'dark', label: 'Escuro', icon: <Moon className="h-4 w-4" /> },
    { value: 'system', label: 'Sistema', icon: <Monitor className="h-4 w-4" /> },
  ];

  return (
    <PermissionGate permission="settings.edit_notifications">
      <div className="space-y-6 max-w-4xl mx-auto">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Configurações</h1>
          <p className="text-muted-foreground">Configure as preferências da plataforma</p>
        </div>

        {/* Aparência */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sun className="h-5 w-5" />
              Aparência
            </CardTitle>
            <CardDescription>Personalize o tema da aplicação</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Tema</Label>
                <p className="text-sm text-muted-foreground">
                  Escolha entre tema claro, escuro ou seguir o sistema
                </p>
              </div>
              <ThemeToggle />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Tema atual</Label>
              <div className="flex flex-wrap gap-2">
                {themes.map((t) => (
                  <Button
                    key={t.value}
                    variant={theme === t.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setTheme(t.value);
                      handleSaveTheme();
                    }}
                    className="gap-2"
                  >
                    {t.icon}
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notificações por Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Notificações por Email
            </CardTitle>
            <CardDescription>Configure quando receber notificações por email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Receber notificações por email</Label>
                <p className="text-sm text-muted-foreground">
                  Ative para receber alertas e notificações importantes por email
                </p>
              </div>
              <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
            </div>
            <Button
              onClick={handleSaveEmailNotifications}
              disabled={updatePreferencesMutation.isPending}
              className="w-full md:w-auto"
            >
              {updatePreferencesMutation.isPending
                ? 'Salvando...'
                : 'Salvar configurações de email'}
            </Button>
          </CardContent>
        </Card>

        {/* Notificações Push */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notificações Push
            </CardTitle>
            <CardDescription>Configure notificações em tempo real no navegador</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Ativar notificações push</Label>
                <p className="text-sm text-muted-foreground">
                  Receba notificações mesmo quando a aplicação estiver em segundo plano
                </p>
              </div>
              <Switch checked={pushNotifications} onCheckedChange={setPushNotifications} />
            </div>
            <Button
              onClick={handleSaveNotifications}
              disabled={updatePreferencesMutation.isPending}
              className="w-full md:w-auto"
            >
              {updatePreferencesMutation.isPending
                ? 'Salvando...'
                : 'Salvar configurações de notificações'}
            </Button>
          </CardContent>
        </Card>

        {/* Idioma */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Idioma e Região
            </CardTitle>
            <CardDescription>Configure o idioma da interface</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="language">Idioma</Label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                title="Selecione o idioma"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en-US">English (US)</option>
                <option value="es-ES">Español</option>
              </select>
            </div>
            <Button
              onClick={handleSaveLanguage}
              disabled={updatePreferencesMutation.isPending}
              className="w-full md:w-auto"
            >
              {updatePreferencesMutation.isPending ? 'Salvando...' : 'Salvar idioma'}
            </Button>
          </CardContent>
        </Card>

        {/* Atualização Automática */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Atualização Automática
            </CardTitle>
            <CardDescription>Configure a atualização automática de dados</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Atualização automática</Label>
                <p className="text-sm text-muted-foreground">
                  Atualizar dados automaticamente em intervalos regulares
                </p>
              </div>
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            </div>
            {autoRefresh && (
              <div className="space-y-2">
                <Label htmlFor="refreshInterval">Intervalo (segundos)</Label>
                <Input
                  id="refreshInterval"
                  type="number"
                  min="10"
                  max="300"
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(Number.parseInt(e.target.value, 10))}
                  className="w-full md:w-48"
                />
                <p className="text-xs text-muted-foreground">
                  Intervalo mínimo: 10 segundos | Máximo: 300 segundos
                </p>
              </div>
            )}
            <Button
              onClick={handleSaveAutoRefresh}
              disabled={updatePreferencesMutation.isPending}
              className="w-full md:w-auto"
            >
              {updatePreferencesMutation.isPending
                ? 'Salvando...'
                : 'Salvar configurações de atualização'}
            </Button>
          </CardContent>
        </Card>

        {/* Informações do Sistema */}
        <Card>
          <CardHeader>
            <CardTitle>Informações do Sistema</CardTitle>
            <CardDescription>Informações sobre a versão e configurações</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Versão:</span>
              <span>1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tema atual:</span>
              <span className="capitalize">{theme}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Idioma:</span>
              <span>{language}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
}
